import React, { useState, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { clientsMutations } from '../../graphql/mutations';
import { clientsQueries } from '../../graphql/queries';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import styled from 'styled-components'
import useAlertStack from 'ui-components/lib/hooks/useAlertStack'
import CircularProgress from '@mui/material/CircularProgress';
import { Bell } from 'lucide-react';

const Wrapper = styled(Box)`
  width: 100%;
  display: flex;
  font-family: 'Roboto';
`

const BoxNotifications = styled(Box)`
  max-width: 400px;
  min-width: 320px;
  padding: 16px 16px 30px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    max-width: 85vw;
    min-width: 280px;
    padding: 16px;
  }

  @media (max-width: 480px) {
    max-width: 90vw;
    min-width: 260px;
  }
` 

const HeaderNotifications = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 7px;
  transition: color 0.3s ease, border-color 0.3s ease;
` 

const MessagesNotifications = styled(Box)`
  width: 100%;
  font-size: 16px;
  font-weight: 400;
  color: var(--text-secondary);
  padding-top: 16px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.5;
  transition: color 0.3s ease;

  @media (max-width: 768px) {
    font-size: 14px;
    padding-top: 12px;
  }
` 

const Notifications = props => {
  const history = useHistory()
  const { showMessage } = useAlertStack()
  const [notificationId, setNotificationId] = useState('');
  const [notifications, setNotifications] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const { loading: notificationsLoading, data: notificationsData, refetch: notificationsRefetch } = useQuery(clientsQueries.GET_NOTIFICATIONS, {
    fetchPolicy: 'cache-and-network'
  })

  const [setReadNotification, { loading: setReadNotificationLoading }] = useMutation(clientsMutations.SET_READ_NOTIFICATION, {
    variables: {
      id: notificationId,
    },
    onCompleted(response) {
      notificationsRefetch()
    },
    onError(error) {
      showMessage({
        title: 'Erro na notificação',
        message: 'Erro ao marcar notificação como lida.',
        color: 'danger',
        position: 'bottom-left',
        time: 4000,
      })
    },
  })  

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationLink = (id, route) => {
    setNotificationId(id)
    history.push(route)
  };

  useEffect(() => {
    if(notificationId) {
      setReadNotification()
    }
  }, [notificationId, setReadNotification]);

  useEffect(() => {
    if(notificationsData?.getNotifications) {
      setNotifications(notificationsData?.getNotifications)
    }
  }, [notificationsData]);


  return (
    <Wrapper style={props.style}>
      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'left', position: 'relative' }}>
        <Button
          onClick={handleClick}
          id="notification-btn"
          style={{
            color: 'var(--text-color)',
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            padding: '4px 8px',
            borderRadius: '4px',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            minWidth: 'auto',
          }}
          startIcon={
            <Bell style={{width: '20px', height: '20px', strokeWidth: 2, color: notifications.length ? 'var(--red-bnz)' : 'var(--text-color)'}} />
          }
        >
          {notifications.length > 0 && (
            <span style={{
              position: 'absolute',
              right: '4px',
              top: '4px',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: 'var(--red-bnz)',
            }} />
          )}
        </Button>
      </Box>
      <Popover
        id={'notificationsPopover'}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'hidden',
            boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
            mt: 1,
            borderRadius: '8px',
            border: '1px solid var(--border-color)',
            overflowY: 'auto',
            maxHeight: '80vh',
            zIndex: 1100,
            '& .MuiMenuItem-root': {
                paddingLeft: '20px',
                paddingRight: '20px',
            },
            '& .MuiMenuItem-root:hover': {
                bgcolor: 'var(--card-bg)',
            },
          },
        }}
        sx={{
          zIndex: 1100,
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <BoxNotifications>
          <HeaderNotifications>
            <Bell style={{width: '16px', height: '16px', strokeWidth: 2}} />
            Notificações
          </HeaderNotifications>
          {notificationsLoading || setReadNotificationLoading ? (
            <Box style={{display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px 20px 10px 20px'}}>
              <CircularProgress sx={{color: 'var(--red-bnz)'}} size={24} /> 
            </Box>
          ) : (
            notifications.length ? (
              notifications.map((item, index) => (
                <MessagesNotifications>
                  <b>{item.senderName}</b>{' '}{item.message}{' '}
                    {item.linkTo && (
                      <Link
                        component="button"
                        variant="body2"
                        style={{color: 'var(--red-bnz)', textDecoration: 'underline', fontWeight: '600', verticalAlign: 'unset'}}
                        onClick={() => handleNotificationLink(item._id, item.linkTo)}
                      >
                        clique aqui
                      </Link>
                    )}
                </MessagesNotifications>
              ))
            ) : ( 
              <MessagesNotifications>
                Você não possui novas notificações
              </MessagesNotifications>
            )  
          )}
        </BoxNotifications>
      </Popover>
    </Wrapper>
  )
}

export default Notifications

